import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { firebaseConfig } from './firebase-config.js';

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const signOutButton = document.querySelector('.sign-out');
const welcomeText = document.querySelector('.welcome-text');
const cards = document.querySelectorAll('.material-card');
const filterButtons = document.querySelectorAll('.filter-button');
const activeFilters = {
  language: 'all',
  level: 'all',
  category: 'all'
};

const rewardTiers = [
  { id: 'bronze', name: 'Bronze', threshold: 0, color: '#b7791f', icon: 'B' },
  { id: 'silver', name: 'Silver', threshold: 200, color: '#9ca3af', icon: 'S' },
  { id: 'gold', name: 'Gold', threshold: 500, color: '#f5b944', icon: 'G' },
  { id: 'platinum', name: 'Platinum', threshold: 1000, color: '#7dd3fc', icon: 'P' },
  { id: 'diamond', name: 'Diamond', threshold: 2000, color: '#c084fc', icon: 'D' }
];

let currentUser = null;

const escapeHtml = value => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const getCurrentTier = points => [...rewardTiers].reverse()
  .find(tier => points >= tier.threshold) || rewardTiers[0];

const normalizeProfile = user => {
  const points = Number(user.coins ?? user.points ?? 0);
  const name = String(user.name || user.displayName || 'Student');
  const profilePhotoUrl = user.profile_photo_url || user.photoURL || '';
  return {
    name,
    profilePhotoUrl: typeof profilePhotoUrl === 'string' && /^https:\/\//i.test(profilePhotoUrl) ? profilePhotoUrl : '',
    studentId: user.student_id || user.studentId || 'IH10294',
    currentLanguage: user.current_language || user.currentLanguage || 'Spanish',
    currentLevel: user.current_level || user.currentLevel || 'B1',
    memberSinceDate: user.member_since_date || user.memberSinceDate || 'Not available',
    membershipStatus: user.membership_status || user.membershipStatus || 'Active',
    rewardPoints: Number.isFinite(points) ? points : 0,
    partnerTier: String(user.partner_tier || user.partnerTier || 'gold').toLowerCase()
  };
};

const getTier = id => rewardTiers.find(tier => tier.id === id) || rewardTiers[2];

const renderStudentCard = (profile, tier) => `
  <article class="digital-card-shell" role="button" tabindex="0" aria-label="iHablar ${escapeHtml(tier.name)} Student Membership Card">
    <div class="digital-card-surface">
      <div class="digital-card-face student-card-front">
        <div class="student-card-main">
          <div class="card-brand-row"><strong>ihablar✓</strong><span class="tier-label" style="--tier-color:${tier.color}"><span class="tier-icon" aria-hidden="true">${tier.icon}</span>${escapeHtml(tier.name)} tier</span></div>
          <div class="student-profile-row"><div class="profile-initials">${profile.profilePhotoUrl ? `<img src="${escapeHtml(profile.profilePhotoUrl)}" alt="" />` : escapeHtml(profile.name.slice(0, 2).toUpperCase())}</div><div><h3>${escapeHtml(profile.name)}</h3><p>Student ID: ${escapeHtml(profile.studentId)}</p></div></div>
          <div class="student-details"><span>${escapeHtml(profile.currentLanguage)} ${escapeHtml(profile.currentLevel)}</span><span>Since: ${escapeHtml(profile.memberSinceDate)}</span><span class="status-text">Status: ${escapeHtml(profile.membershipStatus)}</span></div>
          <small>ihablar.com</small>
        </div>
        <div class="student-card-side"><span class="watermark" aria-hidden="true">✓</span><span class="card-tagline">LEARN. PRACTICE. CONNECT.<br>GROW WITHOUT LIMITS.</span></div>
      </div>
      <div class="digital-card-face digital-card-back student-card-back">
        <div class="back-brand">ihablar✓ <span>STUDENT CARD</span></div>
        <p>This card is your official student membership card. Use it to enjoy exclusive benefits and discounts.</p>
        <div class="verify-row"><span class="verify-mark" aria-hidden="true">✓</span><span>Scan to verify<br><small>Verification available in your account</small></span></div>
        <strong>${escapeHtml(profile.studentId)}</strong>
        <div class="benefit-list"><span>Exclusive Discounts</span><span>Rewards and Points</span><span>Loyalty Benefits</span><span>Partner Discounts</span></div>
        <small>Support: hello@ihablar.com</small>
      </div>
    </div>
  </article>`;

const renderPartnerCard = (profile, tier) => `
  <article class="digital-card-shell" role="button" tabindex="0" aria-label="iHablar ${escapeHtml(tier.name)} Partner Card">
    <div class="digital-card-surface">
      <div class="digital-card-face partner-card-front">
        <div class="partner-card-header"><strong>ihablar✓</strong><span>PARTNER CARD</span></div>
        <div class="partner-name">IHABLAR PARTNER</div>
        <div class="partner-tier" style="--tier-color:${tier.color}"><span class="tier-icon" aria-hidden="true">${tier.icon}</span><span>${escapeHtml(tier.name)} TIER</span></div>
        <div class="partner-identity"><strong>ihablar✓</strong><span>PARTNER</span></div>
      </div>
      <div class="digital-card-face digital-card-back partner-card-back">
        <div class="back-brand">ihablar✓ <span>PARTNER CARD</span></div>
        <p class="partner-mission">TOGETHER. GROWING FUTURES.</p>
        <div class="magnetic-stripe" aria-hidden="true"></div>
        <div class="benefit-list"><span>Community visibility</span><span>Partner discounts</span><span>Shared learning</span><span>Mission support</span></div>
        <strong>${escapeHtml(tier.name)} PARTNER</strong>
        <small>MORE THAN A PARTNER. / A PART OF OUR MISSION.</small>
      </div>
    </div>
  </article>`;

const attachCardFlipHandlers = root => {
  root.querySelectorAll('.digital-card-shell').forEach(card => {
    const flip = () => card.classList.toggle('is-flipped');
    card.addEventListener('click', event => {
      if (!event.target.closest('a,button')) flip();
    });
    card.addEventListener('keydown', event => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      flip();
    });
  });
};

const renderDigitalCards = user => {
  const root = document.querySelector('#digital-cards');
  if (!root) return;
  const profile = normalizeProfile(user);
  const studentTier = getCurrentTier(profile.rewardPoints);
  const partnerTier = getTier(profile.partnerTier);
  root.innerHTML = `<div class="digital-card-item"><h3>Student Membership Card</h3>${renderStudentCard(profile, studentTier)}</div><div class="digital-card-item"><h3>Partner Card</h3>${renderPartnerCard(profile, partnerTier)}</div>`;
  attachCardFlipHandlers(root);
};

const getStorageKey = uid => `plus-material-state-${uid}`;
const getAllState = () => {
  if (!currentUser) return {};
  return JSON.parse(localStorage.getItem(getStorageKey(currentUser.uid)) || '{}');
};

const saveAllState = state => {
  if (!currentUser) return;
  localStorage.setItem(getStorageKey(currentUser.uid), JSON.stringify(state));
};

const getCardState = id => {
  const allState = getAllState();
  return allState[id] || { likes: 0, dislikes: 0, vote: null, comments: [] };
};

const setCardState = (id, updateFn) => {
  const allState = getAllState();
  const state = allState[id] || { likes: 0, dislikes: 0, vote: null, comments: [] };
  updateFn(state);
  allState[id] = state;
  saveAllState(allState);
  return state;
};

const updateCounts = (card, state) => {
  card.querySelector('[data-action="like"] .count').textContent = state.likes;
  card.querySelector('[data-action="dislike"] .count').textContent = state.dislikes;
};

const cardMatchesFilters = card => {
  const language = card.dataset.language;
  const level = card.dataset.level;
  const categories = card.dataset.categories ? card.dataset.categories.split(',') : [];

  if (activeFilters.language !== 'all' && activeFilters.language !== language) return false;
  if (activeFilters.level !== 'all' && activeFilters.level !== level) return false;
  if (activeFilters.category !== 'all' && !categories.includes(activeFilters.category)) return false;
  return true;
};

const applyFilters = () => {
  cards.forEach(card => {
    card.classList.toggle('hidden', !cardMatchesFilters(card));
  });
};

const updateVoteStyle = (card, state) => {
  card.querySelector('[data-action="like"]').classList.toggle('selected', state.vote === 'like');
  card.querySelector('[data-action="dislike"]').classList.toggle('selected', state.vote === 'dislike');
};

const renderComments = (card, state) => {
  const list = card.querySelector('.comment-list');
  list.innerHTML = state.comments
    .map(comment => `<div class="comment-item">${comment}</div>`)
    .join('');
};

const initializeCard = card => {
  const id = card.dataset.materialId;
  const commentPanel = card.querySelector('.comment-panel');
  const commentForm = card.querySelector('.comment-form');
  const commentInput = card.querySelector('.comment-input');
  const commentToggle = card.querySelector('.comment-toggle');

  const updateUI = state => {
    updateCounts(card, state);
    updateVoteStyle(card, state);
    renderComments(card, state);
  };

  const currentState = getCardState(id);
  updateUI(currentState);

  card.querySelector('[data-action="like"]').addEventListener('click', () => {
    const state = setCardState(id, state => {
      if (state.vote === 'like') {
        state.likes -= 1;
        state.vote = null;
      } else {
        if (state.vote === 'dislike') {
          state.dislikes -= 1;
        }
        state.likes += 1;
        state.vote = 'like';
      }
    });
    updateUI(state);
  });

  card.querySelector('[data-action="dislike"]').addEventListener('click', () => {
    const state = setCardState(id, state => {
      if (state.vote === 'dislike') {
        state.dislikes -= 1;
        state.vote = null;
      } else {
        if (state.vote === 'like') {
          state.likes -= 1;
        }
        state.dislikes += 1;
        state.vote = 'dislike';
      }
    });
    updateUI(state);
  });

  commentToggle.addEventListener('click', () => {
    commentPanel.classList.toggle('hidden');
    if (!commentPanel.classList.contains('hidden')) {
      commentInput.focus();
    }
  });

  commentForm.addEventListener('submit', event => {
    event.preventDefault();
    const text = commentInput.value.trim();
    if (!text) return;
    const state = setCardState(id, state => {
      state.comments.unshift(text);
    });
    commentInput.value = '';
    renderComments(card, state);
  });
};

onAuthStateChanged(auth, user => {
  if (!user) {
    window.location.href = 'index.html';
    return;
  }

  currentUser = user;
  welcomeText.textContent = `Hello, ${user.displayName || user.email}`;
  renderDigitalCards(user);
  cards.forEach(initializeCard);
});

filterButtons.forEach(button => {
  button.addEventListener('click', () => {
    const type = button.dataset.filterType;
    const value = button.dataset.filterValue;

    document.querySelectorAll(`.filter-button[data-filter-type="${type}"]`).forEach(item => {
      item.classList.toggle('active', item === button);
    });

    activeFilters[type] = value;
    applyFilters();
  });
});

signOutButton.addEventListener('click', () => {
  signOut(auth);
});
